import type { TeacherExperience } from "~/types/teacherExperience";
import type { OrganizationSummaryResponse } from "~/types/organizations";
import { safeAuthReturnPath } from "~/utils/authRedirect";
import { hasOrganizationPermission } from "~/utils/organizationPermissions";
import { getSchoolMaterialsAccess } from "~/utils/schoolMaterials";

export interface TeacherExperienceUser {
  permissions: { isTeacher: boolean };
  activeOrganization: OrganizationSummaryResponse | null;
  featureFlags?: Record<string, boolean>;
}

// An explicit list prevents private billing/documents routes from acquiring a
// school equivalent just because their prefix looks like a teacher route.
const SHARED_TEACHER_ROUTES = [
  /^\/dashboard\/home\/?$/,
  /^\/dashboard\/(?:books|favorites|attendance|bookings|faq|contact)\/?$/,
  /^\/dashboard\/books\/[a-zA-Z0-9_-]+\/?$/,
  /^\/dashboard\/(?:students|classrooms)\/?$/,
  /^\/dashboard\/(?:students|classrooms)\/[a-zA-Z0-9_-]+\/?$/,
  /^\/dashboard\/account\/?$/,
  /^\/dashboard\/assessments\/?$/,
  /^\/dashboard\/assessments\/[a-zA-Z0-9_-]+(?:\/(?:edit|results))?\/?$/,
  /^\/dashboard\/assessments\/[a-zA-Z0-9_-]+\/attempts\/[a-zA-Z0-9_-]+\/?$/,
  /^\/dashboard\/complementary-activities\/(?:book|lesson)\/[a-zA-Z0-9_-]+\/[a-zA-Z0-9_-]+\/customize\/?$/,
];

export function getTeacherExperience(path: string): TeacherExperience {
  return /^\/school-teacher(?:\/|$)/.test(path)
    ? "school-teacher"
    : "individual";
}

export function schoolTeacherEquivalent(path: unknown): string | null {
  const safe = safeAuthReturnPath(path);
  if (!safe) return null;
  const pathname = safe.split(/[?#]/)[0];
  if (!SHARED_TEACHER_ROUTES.some((pattern) => pattern.test(pathname)))
    return null;
  return `/school-teacher${safe.slice("/dashboard".length)}`;
}

export function teacherPath(
  path: string,
  experience: TeacherExperience = "individual",
): string {
  if (experience === "individual" || !path.startsWith("/dashboard/"))
    return path;
  return schoolTeacherEquivalent(path) || "/school-teacher/home";
}

export function canAccessSchoolTeacher(user: TeacherExperienceUser): boolean {
  const organization = user.activeOrganization;
  return Boolean(
    user.permissions.isTeacher &&
    organization?.can_access &&
    organization.membership_status === "active" &&
    organization.teaching_enabled,
  );
}

export function schoolEntryPath(user: TeacherExperienceUser): string {
  if (
    user.activeOrganization?.can_access &&
    hasOrganizationPermission(
      user.activeOrganization,
      "organization.dashboard.access",
    )
  )
    return "/school-dashboard";
  return canAccessSchoolTeacher(user)
    ? "/school-teacher/home"
    : "/school-select";
}

export function canAccessSchoolTeacherRoute(
  user: TeacherExperienceUser,
  path: string,
): boolean {
  if (!canAccessSchoolTeacher(user)) return false;
  const organization = user.activeOrganization;
  const section = path.split("/")[2] || "home";
  const has = (permission: Parameters<typeof hasOrganizationPermission>[1]) =>
    hasOrganizationPermission(organization, permission);
  if (section === "students") return has("students.view");
  if (section === "classrooms") return has("classrooms.view");
  if (section === "attendance")
    return user.featureFlags?.attendance === true && has("attendance.view");
  if (section === "assessments" || section === "complementary-activities")
    return user.featureFlags?.assessments === true && has("assessments.view");
  if (section === "bookings") return organization?.scheduling_enabled === true;
  if (section === "materials")
    return (
      /^\/school-teacher\/materials\/?$/.test(path) ||
      getSchoolMaterialsAccess(organization).canView
    );
  return ["home", "books", "favorites", "account", "faq", "contact"].includes(
    section,
  );
}
